import { createClient } from '@supabase/supabase-js';

// ==========================================================
// การเชื่อมต่อ SUPABASE & CROSS-SUBDOMAIN SSO AUTH
// ==========================================================
let metaEnv = {};
try {
  // eslint-disable-next-line no-new-func
  metaEnv = Function('return import.meta.env')();
} catch (e) {
  // Ignored in environments that do not support import.meta
}

export const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 
                          process.env.VITE_SUPABASE_URL || 
                          metaEnv.VITE_SUPABASE_URL || 
                          '';

export const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 
                          process.env.VITE_SUPABASE_ANON_KEY || 
                          metaEnv.VITE_SUPABASE_ANON_KEY || 
                          '';

export const COOKIE_DOMAIN = '.fangdent.com';
export const COOKIE_PATH = '/';
export const COOKIE_MAX_AGE = 31536000; // 1 year (seconds)
export const COOKIE_SAME_SITE = 'Lax';
export const CHUNK_SIZE = 3000; // Safe threshold for cookie payload under 4KB limit

/**
 * Checks if the given hostname belongs to fangdent.com or any of its subdomains
 * e.g., 'risk.fangdent.com', 'cssd.fangdent.com', 'dev.fangdent.com', 'fangdent.com'
 */
export function isFangdentDomain(hostname) {
  if (!hostname || typeof hostname !== 'string') return false;
  const cleanHost = hostname.split(':')[0].trim().toLowerCase();
  return cleanHost === 'fangdent.com' || cleanHost.endsWith('.fangdent.com');
}

/**
 * Parse document.cookie string into a key-value dictionary
 */
function parseCookies(cookieString) {
  const map = {};
  if (!cookieString || typeof cookieString !== 'string') return map;
  const parts = cookieString.split(';');
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const eqIdx = trimmed.indexOf('=');
    if (eqIdx === -1) continue;
    const rawKey = trimmed.slice(0, eqIdx).trim();
    const rawVal = trimmed.slice(eqIdx + 1).trim();
    try {
      map[decodeURIComponent(rawKey)] = rawVal;
    } catch {
      map[rawKey] = rawVal;
    }
  }
  return map;
}

/**
 * Read cookie value (supporting single and chunked key.0, key.1 cookies)
 */
function readCookieValue(doc, key) {
  if (!doc) return null;
  const map = parseCookies(doc.cookie || '');

  // 1. Check single cookie key
  if (Object.prototype.hasOwnProperty.call(map, key)) {
    try {
      return decodeURIComponent(map[key]);
    } catch {
      return map[key];
    }
  }

  // 2. Check chunked cookies: key.0, key.1, ...
  if (Object.prototype.hasOwnProperty.call(map, `${key}.0`)) {
    let combined = '';
    let i = 0;
    while (Object.prototype.hasOwnProperty.call(map, `${key}.${i}`)) {
      combined += map[`${key}.${i}`];
      i++;
    }
    try {
      return decodeURIComponent(combined);
    } catch {
      return combined;
    }
  }

  return null;
}

/**
 * Construct cookie removal string
 */
function buildDeleteCookieString(name, { domain, path = COOKIE_PATH, secure = false }) {
  let cookie = `${encodeURIComponent(name)}=`;
  if (domain) cookie += `; domain=${domain}`;
  if (path) cookie += `; path=${path}`;
  cookie += '; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
  if (secure) cookie += '; Secure';
  return cookie;
}

/**
 * Write cookie with automatic chunking if payload approaches ~4KB limits
 */
function writeCookieValue(doc, key, value, { domain, secure }) {
  if (!doc) return;
  const encodedVal = encodeURIComponent(value);
  const options = {
    domain,
    path: COOKIE_PATH,
    maxAge: COOKIE_MAX_AGE,
    sameSite: COOKIE_SAME_SITE,
    secure,
  };

  if (encodedVal.length > CHUNK_SIZE) {
    // Write chunked cookies
    let i = 0;
    while (i * CHUNK_SIZE < encodedVal.length) {
      const chunk = encodedVal.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      let cookieStr = `${encodeURIComponent(`${key}.${i}`)}=${chunk}`;
      if (options.domain) cookieStr += `; domain=${options.domain}`;
      if (options.path) cookieStr += `; path=${options.path}`;
      cookieStr += `; max-age=${options.maxAge}; SameSite=${options.sameSite}`;
      if (options.secure) cookieStr += '; Secure';
      doc.cookie = cookieStr;
      i++;
    }
    // Delete any remaining leftover chunks from previous writes
    for (let c = i; c < i + 10; c++) {
      doc.cookie = buildDeleteCookieString(`${key}.${c}`, options);
      doc.cookie = buildDeleteCookieString(`${key}.${c}`, { ...options, domain: undefined });
    }
    // Delete single non-chunked key
    doc.cookie = buildDeleteCookieString(key, options);
    doc.cookie = buildDeleteCookieString(key, { ...options, domain: undefined });
  } else {
    // Write single cookie
    let cookieStr = `${encodeURIComponent(key)}=${encodedVal}`;
    if (options.domain) cookieStr += `; domain=${options.domain}`;
    if (options.path) cookieStr += `; path=${options.path}`;
    cookieStr += `; max-age=${options.maxAge}; SameSite=${options.sameSite}`;
    if (options.secure) cookieStr += '; Secure';
    doc.cookie = cookieStr;

    // Delete any previous chunked cookies
    for (let c = 0; c < 10; c++) {
      doc.cookie = buildDeleteCookieString(`${key}.${c}`, options);
      doc.cookie = buildDeleteCookieString(`${key}.${c}`, { ...options, domain: undefined });
    }
  }
}

/**
 * Remove cookie and any associated chunks
 */
function deleteCookieValue(doc, key, { domain, secure }) {
  if (!doc) return;
  const options = { domain, secure, path: COOKIE_PATH };
  doc.cookie = buildDeleteCookieString(key, options);
  doc.cookie = buildDeleteCookieString(key, { ...options, domain: undefined });
  for (let c = 0; c < 10; c++) {
    doc.cookie = buildDeleteCookieString(`${key}.${c}`, options);
    doc.cookie = buildDeleteCookieString(`${key}.${c}`, { ...options, domain: undefined });
  }
}

/**
 * Factory for Cross-Subdomain Auth Storage Adapter
 * - Stores auth token in .fangdent.com domain cookie when on *.fangdent.com
 * - Mirrors into window.localStorage for fast local access and redundancy
 * - Seamlessly falls back to localStorage on localhost, IP addresses, and non-domain environments
 */
export function createCrossSubdomainStorage(env = {}) {
  const getHostname = env.getHostname || (() => (typeof window !== 'undefined' ? window.location?.hostname : ''));
  const getProtocol = env.getProtocol || (() => (typeof window !== 'undefined' ? window.location?.protocol : ''));
  const getDoc = env.getDocument || (() => (typeof document !== 'undefined' ? document : null));
  const getLs = env.getLocalStorage || (() => {
    try {
      return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
    } catch {
      return null;
    }
  });

  const memoryFallback = new Map();

  const safeLsGet = (key) => {
    try {
      const ls = getLs();
      return ls ? ls.getItem(key) : memoryFallback.get(key) || null;
    } catch {
      return memoryFallback.get(key) || null;
    }
  };

  const safeLsSet = (key, value) => {
    try {
      memoryFallback.set(key, value);
      const ls = getLs();
      if (ls) ls.setItem(key, value);
    } catch {
      // QuotaExceeded or security restriction
    }
  };

  const safeLsRemove = (key) => {
    try {
      memoryFallback.delete(key);
      const ls = getLs();
      if (ls) ls.removeItem(key);
    } catch {
      // Ignore errors during removal
    }
  };

  return {
    getItem(key) {
      const hostname = getHostname();
      const isFangdent = isFangdentDomain(hostname);
      const doc = getDoc();

      if (isFangdent && doc) {
        // Read from cross-subdomain cookie first
        const cookieVal = readCookieValue(doc, key);
        if (cookieVal !== null && cookieVal !== undefined) {
          // Mirror cookie into localStorage to ensure synchronization
          safeLsSet(key, cookieVal);
          return cookieVal;
        }
        // Fallback to localStorage if cookie not yet set
        return safeLsGet(key);
      }

      // Non-fangdent domain (localhost, IP, etc.) -> use standard localStorage
      return safeLsGet(key);
    },

    setItem(key, value) {
      const hostname = getHostname();
      const isFangdent = isFangdentDomain(hostname);
      const doc = getDoc();
      const secure = getProtocol() === 'https:';

      // Always mirror to localStorage
      safeLsSet(key, value);

      // On fangdent domain, write cross-subdomain cookie with domain=.fangdent.com
      if (isFangdent && doc) {
        writeCookieValue(doc, key, value, {
          domain: COOKIE_DOMAIN,
          secure,
        });
      }
    },

    removeItem(key) {
      const hostname = getHostname();
      const isFangdent = isFangdentDomain(hostname);
      const doc = getDoc();
      const secure = getProtocol() === 'https:';

      // Always remove from localStorage
      safeLsRemove(key);

      // Remove from cookie on fangdent domain (or regardless)
      if (doc) {
        deleteCookieValue(doc, key, {
          domain: isFangdent ? COOKIE_DOMAIN : undefined,
          secure,
        });
      }
    },
  };
}

export const crossSubdomainStorage = createCrossSubdomainStorage();

export const supabase = supabaseUrl && supabaseKey
  ? createClient(supabaseUrl, supabaseKey, {
      auth: {
        storage: crossSubdomainStorage,
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    })
  : null;
